import React from 'react';
import { CircularProgress } from '@mui/material';
import { Psychology as AnalysisIcon } from '@mui/icons-material';
import StandardButton from '../StandardButton';

/**
 * AIButton - A reusable button component for AI-related actions
 * 
 * @param {Object} props
 * @param {boolean} props.loading - Whether the AI action is in progress
 * @param {string} props.loadingText - Text to show when loading (default: "Analyzing...")
 * @param {string} props.children - Button text when not loading
 * @param {function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {React.ReactNode} props.startIcon - Custom start icon (default: Psychology icon)
 * @param {string} props.size - Button size: 'small', 'medium', or 'large' (default: 'medium')
 * @param {...Object} props.otherProps - Additional props passed to the Button
 */
const AIButton = ({
  loading = false,
  loadingText = 'Analyzing...',
  children,
  onClick,
  disabled,
  sx = {},
  startIcon,
  size = 'medium',
  ...otherProps
}) => {
  const defaultIcon = loading ? (
    <CircularProgress size={16} sx={{ color: 'white' }} />
  ) : (
    <AnalysisIcon />
  );

  const aiButtonSx = {
    background: 'linear-gradient(45deg, #2E5266, #FF7A5A) !important',
    '&:hover': {
      background: 'linear-gradient(45deg, #1E3A47, #E56B4A) !important',
    },
    ...sx,
  };

  return (
    <StandardButton
      variant="primary"
      startIcon={startIcon === null ? undefined : (startIcon !== undefined ? startIcon : defaultIcon)}
      onClick={onClick}
      disabled={loading || disabled}
      sx={aiButtonSx}
      size={size}
      {...otherProps}
    >
      {loading ? loadingText : children}
    </StandardButton>
  );
};

export default AIButton;